import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Text,
  Td,
  Tr,
  IconButton,
  Tooltip,
  useColorMode,
} from '@chakra-ui/react';
import { ChevronUpIcon, ChevronDownIcon, BellIcon } from '@chakra-ui/icons';
import { apiSendDocumentReminder } from 'api';
import { PolicyWithDeferredDocs, DeferredDocument } from 'types/documents';
import DocumentListRow from 'components/documents/DocumentListRow';
import RemindClientModal from './RemindClientModal';
import ResolveDocumentModal from './ResolveDocumentModal';
import IgnoreDocumentModal from './IgnoreDocumentModal';
import { useDocumentValidation } from '../hooks/useDocumentValidation';
import { useDocumentOperations } from '../hooks/useDocumentOperations';

interface PolicyCardProps {
  policy: PolicyWithDeferredDocs;
  isExpanded: boolean;
  onToggle: () => void;
}

const PolicyCard = ({ policy, isExpanded, onToggle }: PolicyCardProps) => {
  const navigate = useNavigate();
  const [isRemindModalOpen, setIsRemindModalOpen] = useState(false);
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
  const [isIgnoreModalOpen, setIsIgnoreModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] =
    useState<DeferredDocument | null>(null);
  const { colorMode } = useColorMode();

  const textColor = colorMode === 'light' ? 'secondaryGray.900' : 'white';
  const hoverBg = colorMode === 'light' ? 'gray.200' : 'whiteAlpha.100';

  // Custom hooks
  const { validatingDocs, startValidation, clearValidation } =
    useDocumentValidation({
      documents: policy.deferred_documents,
      policyUuid: policy.uuid,
    });

  const {
    uploadingDocKey,
    isProcessing,
    fileInputRefs,
    handleUploadClick,
    handleFileChange,
    handleResolve,
    handleIgnore,
    handleDeleteFile,
  } = useDocumentOperations({
    policyUuid: policy.uuid,
    onValidationStart: startValidation,
    onValidationClear: clearValidation,
  });

  const handleResolveClick = (doc: DeferredDocument) => {
    setSelectedDocument(doc);
    setIsResolveModalOpen(true);
  };

  const handleIgnoreClick = (doc: DeferredDocument) => {
    setSelectedDocument(doc);
    setIsIgnoreModalOpen(true);
  };

  const confirmResolve = async () => {
    if (selectedDocument) {
      await handleResolve(selectedDocument);
      setIsResolveModalOpen(false);
      setSelectedDocument(null);
    }
  };

  const confirmIgnore = async () => {
    if (selectedDocument) {
      await handleIgnore(selectedDocument);
      setIsIgnoreModalOpen(false);
      setSelectedDocument(null);
    }
  };

  const formattedDate = policy.created_at
    ? new Date(policy.created_at).toLocaleDateString()
    : '-';

  const handlePolicyClick = () => {
    navigate(`/admin/policies/overview/${policy.uuid}`);
  };

  const handleRemindClick = () => {
    setIsRemindModalOpen(true);
  };

  const handleSendReminder = async (policyUuid: string) => {
    await apiSendDocumentReminder(policyUuid);
  };

  return (
    <>
      <Tr
        cursor="pointer"
        _hover={{
          bg: hoverBg,
        }}
      >
        <Td onClick={onToggle}>
          <IconButton
            aria-label="Expand policy"
            icon={isExpanded ? <ChevronDownIcon /> : <ChevronUpIcon />}
            size="sm"
            variant="ghost"
          />
        </Td>
        <Td onClick={handlePolicyClick}>
          <Flex align="center">
            <Text color={textColor} fontSize="sm" fontWeight="700">
              {policy.client_name || '-'}
            </Text>
          </Flex>
        </Td>
        <Td onClick={handlePolicyClick}>
          <Flex align="center">
            <Text color={textColor} fontSize="sm" fontWeight="700">
              {policy.binder_number || '-'}
            </Text>
          </Flex>
        </Td>
        <Td onClick={handlePolicyClick}>
          <Flex align="center">
            <Text color={textColor} fontSize="sm" fontWeight="700">
              {formattedDate}
            </Text>
          </Flex>
        </Td>
        <Td onClick={handlePolicyClick}>
          <Flex align="center">
            <Text color={textColor} fontSize="sm" fontWeight="700">
              {policy.deferred_documents.length}
            </Text>
          </Flex>
        </Td>
        <Td>
          <Tooltip label="Remind client" hasArrow>
            <IconButton
              aria-label="Remind client"
              icon={<BellIcon />}
              colorScheme="blue"
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleRemindClick();
              }}
            />
          </Tooltip>
        </Td>
      </Tr>

      {isExpanded && (
        <Tr>
          <Td colSpan={6} p={0} bg="gray.50">
            <Box p="16px">
              <Text fontSize="sm" fontWeight="600" mb="12px" color="gray.600">
                Pending Documents ({policy.deferred_documents.length})
              </Text>
              <Box>
                {policy.deferred_documents.map((doc) => {
                  const docKey = `${doc.validation_key}-${doc.item_key}`;
                  const isUploading = uploadingDocKey === docKey;
                  const validationInfo = validatingDocs[docKey];

                  return (
                    <Box key={docKey} mb="8px">
                      <DocumentListRow
                        doc={doc}
                        mode="broker"
                        isUploading={isUploading}
                        validationInfo={validationInfo}
                        fileInputRef={(el) =>
                          (fileInputRefs.current[docKey] = el)
                        }
                        onUploadClick={() => handleUploadClick(docKey)}
                        onFileChange={(e) => handleFileChange(e, doc)}
                        onResolveClick={() => handleResolveClick(doc)}
                        onIgnoreClick={() => handleIgnoreClick(doc)}
                        onDeleteFile={(fileId) => handleDeleteFile(doc, fileId)}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Td>
        </Tr>
      )}

      {/* Remind Client Modal */}
      <RemindClientModal
        isOpen={isRemindModalOpen}
        onClose={() => setIsRemindModalOpen(false)}
        clientName={policy.client_name}
        clientEmail={policy.client_email}
        policyUuid={policy.uuid}
        onSendReminder={handleSendReminder}
      />

      {/* Resolve Document Modal */}
      <ResolveDocumentModal
        isOpen={isResolveModalOpen}
        onClose={() => {
          setIsResolveModalOpen(false);
          setSelectedDocument(null);
        }}
        document={selectedDocument}
        isProcessing={isProcessing}
        onConfirm={confirmResolve}
      />

      {/* Ignore Document Modal */}
      <IgnoreDocumentModal
        isOpen={isIgnoreModalOpen}
        onClose={() => {
          setIsIgnoreModalOpen(false);
          setSelectedDocument(null);
        }}
        document={selectedDocument}
        isProcessing={isProcessing}
        onConfirm={confirmIgnore}
      />
    </>
  );
};

export default PolicyCard;
